<template>
  <div>
    <div v-if="$route.query.phone" class="register-page" :class="{'modal-showed': isModalShow}">
      <status-bar :title="$t('register.title')" />
      <validation-observer
        v-slot="{ valid, invalid }"
        ref="observer"
        slim
        tag="div"
        class="register-page-form"
      >
        <template v-if="!isPartialMatch">
          <register-step
            :user-data-is-valid="isUserDataValid"
            :backend-errors="backendErrors"
            :form-data="formData"
            :register-source-manual="registerSourceManual"
            @update-field="updateField"
            @update-register-source="registerSourceManual = $event"
          />
          <div class="register-page-form-actions">
            <pc-button
              v-if="!isUserDataValid"
              variant="dark"
              :disabled="!valid || isQueryInvalid || isVerificationModalDisplayed"
              @click="updateUserData"
            >
              {{ $t('register.registration') }}
            </pc-button>
            <pc-button
              v-else
              variant="dark"
              :disabled="!valid || isQueryInvalid"
              @click="register"
            >
              {{ $t(getRegisterButtonText) }}
            </pc-button>
          </div>
        </template>
        <validation-observer v-else class="register-page-match">
          <div class="register-page-match__title">
            {{ $t('duplicate-check.phone-or-email-is-used') }}
          </div>
          <div class="register-page-match-actions">
            <pc-button
              class="register-page-match__action-btn"
              @click="contactManager"
            >
              {{ $t('duplicate-check.contact-the-manager') }}
            </pc-button>
          </div>
          <div v-if="managerModalError" class="register-page-match__error">
            {{ $t(managerModalError) }}
          </div>
        </validation-observer>
      </validation-observer>
      <icu-loader v-if="isLoading" />
      <icu-support>
        {{ $t('common.if-you-have-questions') }}
        <a href="mailto:support@icu.ua" target="_blank">support@icu.ua</a>
      </icu-support>
    </div>
    <div v-else>
      <resident-register
        v-if="showResidentRegister"
        :backend-errors="backendErrors"
        @start-register="startResidentRegister"
      />
      <modal-wrapper v-if="showDataSourceModal">
        <modal-container :show-close="false">
          <div class="register-page__modal-header">
            <img src="@/assets/img/logo.svg" alt="logo" class="page__logo" />
            <language-switcher />
          </div>
          <h3 class="source-modal-title">
            {{ $t('qr-code.choose-register-source') }}
          </h3>
          <div class="register-page__modal-buttons vertical">
            <pc-button
              v-if="settings.SHOW_BUTTON_DIIA_VERIFICATION"
              variant="dark"
              :title=" $t('register.diya-registration-btn-hint')"
              @click="setSource('SHARING')"
            >
              {{ $t('register.diya-registration-btn-text') }}
            </pc-button>
            <pc-button
              v-if="settings.SHOW_BUTTON_BANKID_VERIFICATION"
              variant="void"
              :title="$t('register.bankid-registration-btn-hint')"
              @click="setSource('BANKID')"
            >
              {{ $t('register.bankid-registration-btn-text') }}
            </pc-button>
          </div>
        </modal-container>
      </modal-wrapper>
    </div>

    <modal-wrapper v-if="showModal">
      <modal-container :show-close="true" @close="closeTab">
        <div class="register-page__modal-img-wrap">
          <img
            src="@/assets/img/invalid.png"
            class="register-page__modal-img"
            width="200"
            height="160"
          />
        </div>
        <div class="register-page__modal-text text-left">
          {{ $t('notifications.something-went-wrong') }}
        </div>
        <div class="register-page__modal-sub-text">
          {{ $t('notifications.something-went-wrong-recheck-phone') }}
        </div>

        <pc-button variant="dark" @click="closeTab">
          {{ $t('application-form.modal.button') }}
        </pc-button>
      </modal-container>
    </modal-wrapper>
    <non-resident-modal v-if="showNonResidentModal"></non-resident-modal>
    <template v-if="isVerificationModalDisplayed">
      <verification-modal
        v-if="!isEmailConfirmed"
        key="email"
        type="email"
        :is-resident="isResident"
        :error="$t(verificationModalError)"
        :show-resend="showResendButton"
        :block-time="blockTime"
        :show-contacts="showVerificationEmailModalContacts"
        @resend="resendCode('email')"
        @confirm="verifyEmail"
      >
        <template #title>
          <span v-html="$t('verification-modal.code-was-sent-to-email', {email: verificationData.email})" />
        </template>
      </verification-modal>
      <verification-modal
        v-if="isEmailConfirmed && !isPhoneConfirmed"
        key="phone"
        type="phone"
        :is-resident="isResident"
        :error="$t(verificationModalError)"
        :show-resend="showResendButton"
        :block-time="blockTime"
        :show-contacts="showVerificationPhoneModalContacts"
        @resend="resendCode('phone')"
        @confirm="verifyPhone"
      >
        <template #title>
          <span v-html="$t('verification-modal.code-was-sent-to-phone', {phone: verificationData.phone})" />
        </template>
      </verification-modal>
    </template>

    <common-modal
      v-if="isManagerModalDisplayed"
      :heading="$t('duplicate-check.manager-has-been-notified')"
      :img-src="require('@/assets/img/components/modals/phone.png')"
      @close="$router.push({name: 'login'})"
    />
  </div>
</template>

<script>
import StatusBar from '@/components/views/RegisterPage/StatusBar.vue'
import RegisterStep from '@/components/views/RegisterPage/steps/RegisterStep.vue'
import PcButton from '@/components/common/PcButton.vue'
import IcuLoader from '@/components/common/IcuLoader.vue'
import VerificationModal from '@/components/modals/VerificationModal.vue'
import CommonModal from '@/components/modals/CommonModal.vue'
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import IcuSupport from '@/components/common/IcuSupport.vue'
import { fromUnixTime, differenceInSeconds } from 'date-fns'
import { constants } from '@/constants.js'
import { api } from '@/api/api.js'
import ModalWrapper from '@/components/modals/ModalWrapper'
import ModalContainer from '@/components/modals/ModalContainer'
import NonResidentModal from '@/components/modals/NonResidentModal'
import LanguageSwitcher from '@/components/common/LanguageSwitcher'
import TextInput from '@/components/inputs/TextInput'
import FormGroup from '@/components/views/RegisterPage/FormGroup'
import ResidentRegister from '@/components/views/RegisterPage/ResidentRegister'
import DiyaCheckData from '@/components/views/RegisterPage/DiyaCheckData'
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'RegisterStart',
  components: {
    DiyaCheckData,
    ResidentRegister,
    FormGroup,
    TextInput,
    LanguageSwitcher,
    NonResidentModal,
    ModalContainer,
    ModalWrapper,
    IcuSupport,
    CommonModal,
    ValidationObserver,
    ValidationProvider,
    VerificationModal,
    IcuLoader,
    PcButton,
    RegisterStep,
    StatusBar
  },
  data() {
    return {
      formSettings: {},
      registerDatasource: '',
      showNonResidentModal: false,
      formData: {
        phone: '',
        email: '',
        password1: '',
        password2: '',
        register_source: [],
      },
      isResident: true,
      isUserDataValid: true,
      showModal: false,
      registerSourceManual: '',
      backendErrors: {},
      verificationData: {
        phone: '',
        email: ''
      },
      verificationModalError: '',
      showDataSourceModal: false,
      isPhoneConfirmed: false,
      isEmailConfirmed: false,
      blockTime: 0,
      codeLifeTime: 0,
      isVerificationModalDisplayed: false,
      showResidentRegister: true,
      showResendButton: false,
      isLoading: false,
      isManagerModalDisplayed: false,
      isPartialMatch: false,
      isQueryInvalid: false,
      smsIsAvailable: true,
      managerModalError: '',
      qrCodeData: {},
      sharingStatus: '',
      showVerificationPhoneModalContacts: false,
      showVerificationEmailModalContacts: false
    }
  },
  computed: {
    ...mapGetters(['settings']),
    isModalShow() {
      return this.showNonResidentModal
          || this.showModal
          || this.isManagerModalDisplayed
          || this.isVerificationModalDisplayed
    },
    getRegisterButtonText(){
      return this.formData.avoid_phone_check ?  'application-form.continue' :'register.register'
    }
  },
  async mounted() {
    await this.setAppSettings()
    localStorage.removeItem('access_token')
    const {data} = await api.profile.settings()
    this.formSettings = data
    await this.startRegister()
  },
  methods: {
    ...mapActions(['setAppSettings']),
    closeTab(){
      this.isUserDataValid = false
      this.showModal = false
    },
    async updateUserData(){
      await this.startRegister(false)
    },
    async startRegister(useQueryParams = true) {

      if( useQueryParams) {
        const {phone, email, is_resident, interface_language, register_datasource, set_password} = this.$route.query
        this.isResident = is_resident !== 'false'
        if( !phone || !email ) {
          return
        }
        if( this.formSettings.BLOCK_NONRESIDENT && is_resident === 'false') {
          this.showNonResidentModal = true
          return
        }

        this.formData.phone = phone ? Number(phone).toString() : ''
        this.formData.register_datasource = register_datasource
        this.formData.set_password = set_password
        this.formData.email = email.toLowerCase()
        this.formData.is_resident = is_resident
        this.verificationData.phone = this.formData.phone
        this.verificationData.email = this.formData.email
      } else {
        if(!this.isResident) {
          this.formData.register_datasource = 'BANKID'
          let query = JSON.parse(JSON.stringify(this.$route.query))
          if (query.phone !== this.formData.phone) {
            query.phone = this.formData.phone
            query.email = this.formData.email
            await this.$router.replace({query})
            this.$set(this.formData, 'avoid_phone_check', true)
          } else {
            this.isQueryInvalid = true
          }
        }
      }
      if (!this.formData.set_password) {
        if(!this.isResident || !this.formData.register_datasource) {
          try {
            const {data} = await api.auth.register(this.formData)
            this.formData.phone = data.phone
            this.formData.email = data.email
            this.formData.is_resident = data.is_resident
            this.formData.interface_language = data.interface_language
            this.formData.register_datasource = data.register_datasource
            this.verificationData.phone = data.phone
            this.verificationData.email = data.email
            this.isVerificationModalDisplayed = true
            this.isUserDataValid = true
            this.smsIsAvailable = data.sms_otp_is_available

            return true
          } catch (e) {
            if (e.response.data.sms_otp_is_available === 'False') {
              this.isPhoneConfirmed = true
            }
            this.isQueryInvalid = true
            this.isVerificationModalDisplayed = false
            this.processCodeError(e.response.data, 'notifications.something-went-wrong-recheck')
          }
        }
      }
    },
    async contactManager() {
      try {
        await api.auth.mailManager({
          email: this.formData.email,
          phone: this.formData.phone
        })

        this.managerModalError = ''
        this.isManagerModalDisplayed = true
      } catch (e) {
        if (e.response.status === 429) {
          this.managerModalError = 'register.already-requested'
        } else {
          this.$toast.error(this.$t('notifications.something-went-wrong'))
        }
      }
    },

    updateField({formKey, value}) {
      this.formData[formKey] = value
      this.backendErrors[formKey] = []
    },
    async resendCode(confirm_type) {
      this.verificationModalError = ''

      try {
        await api.auth.sendCode({
          ...this.verificationData,
          confirm_type
        })
        this.showResendButton = false
      } catch (e) {
        const error = e.response.data

        if (error._error_code) {
          this.processCodeError(error, error.message)
        } else {
          this.verificationModalError = error.message ?? 'notifications.something-went-wrong'
        }
      }
    },
    async setSource(value){
      try {
        this.formData.register_datasource = value
        await api.auth.updateRegisterDataSource({register_datasource: value})
        if( value === 'SHARING'){
          await this.$router.push({name: 'diya-qr-code', params: {skipCheck: true}})
        } else {
          window.location = `https://id.online.icu/?interface_language=${this.$i18n.locale}&phone=${this.formData.phone}&email=${this.formData.email}`
        }
      }catch (err) {
        console.log(err)
      }
    },
    async register() {
      this.isLoading = true
      try {
        let registerSource = this.formData.register_source.map(source => this.$t(source.name)).join(',')

        if (this.registerSourceManual) {
          registerSource += `, ${this.registerSourceManual}`
        }
        const { data } = await api.auth.setPassword({
          ...this.formData,
          register_source: registerSource
        })

        const requestTime = Date.now()
        const accessTTL = Number(localStorage.getItem('access_ttl_limit'))
        const refreshTTL = Number(localStorage.getItem('refresh_ttl_limit'))
        await localStorage.setItem('access_ttl', requestTime + accessTTL)
        localStorage.setItem('refresh_ttl', requestTime + refreshTTL)
        localStorage.setItem('access_token', data.access)
        localStorage.setItem('refresh_token', data.refresh)
        localStorage.setItem('scope', data.scope)
        if(this.formData.register_datasource === 'SHARING'){
          await this.$router.push({name: 'register-check-data'})
        } else {
          await this.$router.push({name: 'register-form'})
        }
      } catch (e) {
        const error = e.response.data

        if (error._error_code) {
          this.processCodeError(error, error.message)
        } else {
          this.backendErrors = e.response.data
        }
      } finally {
        this.isLoading = false
      }
    },
    startResidentRegister({email, phone, register_datasource}){
      this.verificationData.phone = this.formData.phone = phone
      this.verificationData.email = this.formData.email = email
      this.formData.register_datasource = register_datasource
      this.startRegister(false)
    },
    async verifyPhone(code) {
      if(this.smsIsAvailable) {
        try {
          const { data } = await api.auth.checkPhone({
            code
          })
          if( this.isResident) {
              this.showDataSourceModal = true
              this.showResidentRegister = false
          }

          this.verificationModalError = ''
          this.isPhoneConfirmed = true
          this.isVerificationModalDisplayed = false

        } catch (e) {
          const error = e.response.data
          if (error._error_code) {
            this.processCodeError(error, null, 'phone')
          } else {
            this.verificationModalError = error.message ?? 'notifications.something-went-wrong'
          }
        }
      }
    },


    async verifyEmail(code) {
      try {
        await api.auth.checkEmail({
          code
        })
        this.isEmailConfirmed = true
        this.verificationModalError =''
        if(!this.smsIsAvailable) {
          this.isPhoneConfirmed = true
          if (this.isResident) {
            this.showDataSourceModal = true
            this.showResidentRegister = false
          }
          this.isVerificationModalDisplayed = false
        }
      } catch (e) {
        const error = e.response.data

        if (error._error_code) {
          this.processCodeError(error, null, 'email')
        } else {
          this.verificationModalError = error.message ?? 'notifications.something-went-wrong'
        }
      }
    },
    handleBlockTime(blockUntil, serverTime) {
      let seconds = differenceInSeconds(fromUnixTime(blockUntil), fromUnixTime(serverTime))
      this.blockTime = seconds
      setTimeout(() => {this.blockTime = 0},0)
      const minutes = (Math.floor(seconds / 60)).toString()
      seconds = (seconds % 60).toString()
      this.$toast.error(this.$t('notifications.next-try-will-be-available', { minutes: minutes.padStart(2, '0'), seconds: seconds.padStart(2, '0')}))
    },
    processCodeError(error, customErrorMessage = '', type) {
      switch (error._error_code) {
        case constants.ERROR_CODES.invalidPhone:
          if(!this.isResident) {
            this.showModal = true
            this.$refs.observer?.setErrors({phone:['']})
          }
          this.isQueryInvalid = false
          this.backendErrors = {phone: 'notifications.something-went-wrong-recheck-phone'}
          break
        case constants.ERROR_CODES.full:
          this.$router.push({name: 'login'})
          break
        case constants.ERROR_CODES.fullSent:
          console.log(error.doubles)
          if( error.doubles?.profile_status === 'NOLOGIN') {
            this.$router.push({name: 'diya-qr-code', query: {state: 'notUniqueData'}})
          } else if (error.doubles?.profile_status === 'EXISTING') {
            this.$router.push({name: 'diya-qr-code', query: {state: 'success'}})
          } else if (error.doubles?.profile_status === 'LAUNCHING') {
            this.$router.push({name: 'diya-qr-code', query: {state: 'waiting'}})
          } else {
            this.$router.push({name: 'register-final-page', query: {alreadySent: '1'}})
          }
          break
        case constants.ERROR_CODES.partial:
          this.isPartialMatch = true
          this.isVerificationModalDisplayed = false
          break
        case constants.ERROR_CODES.wrongCode:
          this.verificationModalError = 'common.wrong-code'
          if (!isNaN(error.block_until)) {
            this.handleBlockTime(error.block_until, error.server_time)
            if(type === 'phone') {
              this.showVerificationPhoneModalContacts = true
            } else {
              this.showVerificationEmailModalContacts = true
            }
          }
          if (+error.tries >= 3) {
            this.showResendButton = true
          }

          break
        case constants.ERROR_CODES.notFound:
          this.verificationModalError = 'common.code-not-found'
          this.showResendButton = true
          break
        case constants.ERROR_CODES.block:
          this.handleBlockTime(error.block_until, error.server_time)
          this.verificationData.phone = '+' + this.formData.phone
            this.showResendButton = true
            this.isVerificationModalDisplayed = true
          break
        case constants.ERROR_CODES.forbidden:
          this.$toast.error(this.$t('notifications.register-email-forbidden', {email: this.$route.query.email.split('@')[1]}))
          break

        case constants.ERROR_CODES.registrationImpossible:
          this.$toast.error(this.$t(customErrorMessage || 'notifications.something-went-wrong'))
          break
        default:
          this.backendErrors = error
          this.$toast.error(this.$t(customErrorMessage || 'notifications.something-went-wrong'))
      }
    },
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/views/register-common.scss";
.source-modal-title {
  text-align: center;
  padding: 30px 0;
}
</style>
